import type { GetStaticPathsResult } from 'next';
import { ConfigProvider } from 'shared/config/ConfigContext';
import flightData from 'shared/seo/data/flight.json';
import { LinkTree } from 'shared/seo/LinkTree';
import { PopularCities } from 'shared/seo/PopularCities';
import { PopularCountries } from 'shared/seo/PopularCountries';
import { validSeoPath } from 'shared/util/formatting';
import { DestinationPage as OldDestinationPage } from '@/components/DestinationPage';
import { cityLevel1Factory, flightSearchFactory } from '@/components/factory';
import { DestinationPage as NewDestinationPage } from '@/components/NewDestinationPage';
import { config, feConfig } from '@/config/index.mjs';
import { flightCities, flightCountries, flightMapping, getCountriesForDestination } from '@/data';
import { verticals } from '@/data/verticals';
import type { CityPageProps, SeoPageContext } from '@/interfaces/pages';
import { pageContentUrl, pageFor } from '@/lib/flightContent';

const allPagesMetadataUrl = config.get('flight.allDestinationPagesMetadata');

const fixParams = (params) => {
    const countryLevel1 = validSeoPath(params.countryLevel1);
    const cityLevel1 = validSeoPath(params.cityLevel1);

    return { countryLevel1, cityLevel1 };
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
    const pageCombinations = await fetch(allPagesMetadataUrl)
        .then((res) => (res.ok ? res.json() : Promise.reject(`(${res.status}) ${res.statusText}: ${res.url}`)))
        .catch((err) => {
            console.error(err);
            return err;
        });

    const destinations = pageCombinations.map((page: { place: string; country: string }) => ({
        params: { cityLevel1: page.place, countryLevel1: page.country },
    }));

    return {
        paths: destinations,
        // TODO remove this when the prod database has all the entries that the dev database has.
        // I.e. the build pregenerates pages for the entries in the prod database and for entries
        // only in the dev database it will generate them on the fly with fallback set to `blocking`.
        fallback: 'blocking',
    };
}

export async function getStaticProps(context: SeoPageContext) {
    const { countryLevel1, cityLevel1 } = fixParams(context.params);
    const page = await pageFor(pageContentUrl.forDestination(countryLevel1, cityLevel1));

    const destination = flightCities.find(
        (place) => place.cityPath === cityLevel1.toLowerCase() && place.countryPath === countryLevel1.toLowerCase(),
    );
    const destinationCountry = flightCountries.find((place) => place.countryPath === countryLevel1.toLowerCase());

    if (!page.htmlContent && (!destination || !destinationCountry)) {
        return {
            notFound: true,
        };
    }

    const popularCities = (flightMapping[destinationCountry.id] ?? []).filter((mapped) => mapped.city !== destination.city).slice(0, 11);
    const popularCountries = getCountriesForDestination(destinationCountry, flightCountries);

    return {
        props: {
            destination: {
                ...destination,
                continent: destinationCountry.continent,
            },
            content: page.htmlContent ?? null,
            popularCities,
            popularCountries,
            config: feConfig,
        },
        revalidate: 3600,
    };
}

const CityLevel1 = ({ destination, content, popularCities, popularCountries, config }: CityPageProps) => {
    const factory = cityLevel1Factory(destination, flightData, flightSearchFactory());

    const DestinationPage = content ? NewDestinationPage : OldDestinationPage;

    return (
        <ConfigProvider config={config}>
            <DestinationPage factory={factory} vertical={verticals.flight} data={flightData}>
                {content ? <div className="mb-64 mx-8" dangerouslySetInnerHTML={{ __html: content }} /> : null}
                <PopularCities
                    destination={destination}
                    cities={popularCities}
                    cdnUrl={flightData.cdnPath}
                    verticalUrl={flightData.basePath}
                    lang={flightData.lang}
                />
                <PopularCountries
                    destination={destination}
                    countries={popularCountries}
                    cdnUrl={flightData.cdnPath}
                    verticalUrl={flightData.basePath}
                    lang={flightData.lang}
                />
                <LinkTree
                    heading={flightData.lang.linkTree.heading}
                    description={flightData.lang.linkTree.description}
                    data={flightData.linkTree}
                    url={flightData.basePath}
                />
            </DestinationPage>
        </ConfigProvider>
    );
};

export default CityLevel1;
